import { DownloadIcon, SearchIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Input } from '@sixam/ui/input';
import { Text } from '@sixam/ui/text';
import {
  normalizeEmail,
  normalizeOptionalText,
  type ContactWithVenue,
} from '@sixam/shared/contact-core';

function matchesSearch(contact: ContactWithVenue, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return [contact.name, contact.email, contact.phone, contact.source, contact.venues?.name].some(
    (value) => value?.toLowerCase().includes(normalized)
  );
}

function formatUpdatedAt(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
  }).format(new Date(value));
}

export function AdminContactsList({
  contacts,
  error,
  isExporting,
  isLoading,
  onExport,
  searchQuery,
}: Readonly<{
  contacts: ContactWithVenue[];
  error?: string | null;
  isExporting?: boolean;
  isLoading?: boolean;
  onExport: () => void;
  searchQuery?: string;
}>) {
  const [localQuery, setLocalQuery] = React.useState('');
  const query = searchQuery ?? localQuery;
  const filtered = contacts.filter((contact) => matchesSearch(contact, query));
  const optedInCount = contacts.filter((contact) => contact.consent_status === 'opted_in').length;
  const showInlineSearch = searchQuery === undefined;

  return (
    <View className="gap-5">
      <Text variant="muted">
        {contacts.length} total · {optedInCount} opted in
      </Text>

      <View className="gap-3 sm:flex-row sm:items-center sm:justify-between">
        {showInlineSearch ? (
          <View className="min-w-0 flex-1">
            <View className="relative">
              <Input
                autoCapitalize="none"
                editable={!isLoading}
                placeholder="Search contacts"
                value={localQuery}
                onChangeText={setLocalQuery}
              />
              <View className="absolute right-4 top-3">
                <SearchIcon color="white" size={18} />
              </View>
            </View>
          </View>
        ) : null}
        <Button disabled={isLoading || isExporting || contacts.length === 0} onPress={onExport}>
          <DownloadIcon color="white" size={18} />
          <Text>{isExporting ? 'Exporting...' : 'Export CSV'}</Text>
        </Button>
      </View>

      {isLoading ? <ActivityIndicator /> : null}

      {error ? (
        <Text className="text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}

      {!isLoading && filtered.length === 0 ? (
        <View className="rounded-md border-2 border-border bg-card p-5">
          <Text variant="large">{contacts.length === 0 ? 'No contacts yet' : 'No matches'}</Text>
          <Text variant="muted">
            {contacts.length === 0
              ? 'Signup and event registration contacts will appear here.'
              : 'Try a name, email, phone, or source.'}
          </Text>
        </View>
      ) : null}

      {filtered.length > 0 ? (
        <View className="overflow-hidden rounded-md border-2 border-border bg-card">
          {filtered.map((contact, index) => (
            <View
              key={contact.id}
              className="gap-3 border-border p-4 sm:flex-row sm:items-center sm:justify-between"
              style={{ borderTopWidth: index === 0 ? 0 : 1 }}
            >
              <View className="min-w-0 flex-1 gap-1">
                <Text variant="large" numberOfLines={1}>
                  {normalizeOptionalText(contact.name) ?? normalizeEmail(contact.email) ?? 'Runner'}
                </Text>
                <Text variant="muted" numberOfLines={1} selectable>
                  {contact.email ?? 'No email'} · {contact.phone ?? 'No phone'}
                </Text>
                {contact.venues?.name ? (
                  <Text variant="mono" numberOfLines={1}>
                    {contact.venues.name}
                    {contact.venues.google_places_id ? ' · Google Places' : ''}
                  </Text>
                ) : null}
              </View>
              <View className="gap-1 sm:items-end">
                <Text variant="label" className="text-muted-foreground">
                  {contact.consent_status.replace('_', ' ')}
                </Text>
                <Text variant="mono">
                  {contact.source} · {formatUpdatedAt(contact.updated_at)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}
